import * as THREE from 'three';

/**
 * Painted hair: close-cropped styles (low cut, fade, buzz) and beards drawn onto the skin
 * rather than as hair meshes. A mask is rendered once into the body's UV space: each vertex gets
 * a coverage value from its position on the head (smooth hairline / beard line), and the
 * fragment shader breaks it into stubble. The skin material then mixes in the hair colour.
 */

export type PaintedHair = 'none' | 'lowcut' | 'fade' | 'buzz' | 'afro' | 'taper' | 'cornrows' | 'under'; // ('under': dark scalp under twists or a wig, well inside the hairline so the hair always covers its edge) // ('afro'/'taper': the hairline under those shells, their own shapes)
export type Beard = 'none' | 'stubble' | 'short' | 'full';
/** stockings painted on the skin (a fishnet body stocking: legs and body, never the arms, hands or head) */
export type Stockings = 'none' | 'fishnet';

interface HeadFrame {
  centre: THREE.Vector3;
  radius: number;
  /** landmarks (MakeHuman units) for the beard, which follows the face rather than the skull */
  noseTipY: number;
  chinY: number;
  halfWidth: number;
}

/** Head centre, size and face landmarks from the base mesh (MakeHuman units). */
export function headFrame(base: Float32Array, bodyVerts: Uint16Array): HeadFrame {
  let top = -Infinity;
  for (const i of bodyVerts) top = Math.max(top, base[i * 3 + 1]);
  const c = new THREE.Vector3(); let n = 0;
  const head: number[] = [];
  for (const i of bodyVerts) if (base[i * 3 + 1] > top - 2.4) { c.x += base[i * 3]; c.y += base[i * 3 + 1]; c.z += base[i * 3 + 2]; n++; head.push(i); }
  c.divideScalar(n);
  // nose tip: the most forward vertex of the face
  let tip = head[0];
  for (const i of head) if (base[i * 3 + 2] > base[tip * 3 + 2]) tip = i;
  const noseTipY = base[tip * 3 + 1];
  // chin: lowest vertex that is still well forward of the neck, near the midline
  let chinY = noseTipY;
  for (const i of head) if (Math.abs(base[i * 3]) < 0.25 && base[i * 3 + 2] > base[tip * 3 + 2] - 0.9) chinY = Math.min(chinY, base[i * 3 + 1]);
  let halfWidth = 0;
  for (const i of head) if (Math.abs(base[i * 3 + 1] - noseTipY) < 0.15) halfWidth = Math.max(halfWidth, Math.abs(base[i * 3] - c.x));
  c.y = top - 1.15; // roughly between the eyes and the crown
  return { centre: c, radius: 1.2, noseTipY, chinY, halfWidth };
}

/** Where a body stocking covers (0..1) a base-mesh vertex: ankle to chest, arms and head left bare. */
export function stockingCover(base: Float32Array, i: number, bounds: { minY: number; maxY: number }): number {
  const h = bounds.maxY - bounds.minY, y = (base[i * 3 + 1] - bounds.minY) / h, x = Math.abs(base[i * 3]);
  if (y < 0.045 || y > 0.73) return 0; // (the feet are in boots; above the bust it stops)
  if (y > 0.5 && x > h * 0.105) return 0; // (above the hips, the arms hang out to the sides: not them)
  return 1;
}
export function bodyBounds(base: Float32Array, bodyVerts: Uint16Array): { minY: number; maxY: number } {
  let minY = Infinity, maxY = -Infinity;
  for (const i of bodyVerts) { minY = Math.min(minY, base[i * 3 + 1]); maxY = Math.max(maxY, base[i * 3 + 1]); }
  return { minY, maxY };
}

const smooth = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** Each vertex's direction from the head's centre (xyz) and whether it's on the head at all (w): for per-pixel lines. */
export function headDirs(base: Float32Array, r2s: Uint16Array, f: HeadFrame): Float32Array {
  const out = new Float32Array(r2s.length * 4);
  for (let k = 0; k < r2s.length; k++) {
    const i = r2s[k], x = base[i * 3] - f.centre.x, y = base[i * 3 + 1] - f.centre.y, z = base[i * 3 + 2] - f.centre.z;
    const len = Math.hypot(x, y, z) || 1;
    if (len > f.radius * 2.2) continue;
    out[k * 4] = x / len; out[k * 4 + 1] = y / len; out[k * 4 + 2] = z / len; out[k * 4 + 3] = 1;
  }
  return out;
}

/** Per-vertex coverage (0..1) of painted hair and beard, from direction on the head. */
export function coverage(base: Float32Array, i: number, f: HeadFrame, hair: PaintedHair, beard: Beard): [number, number] {
  const x = base[i * 3] - f.centre.x, y = base[i * 3 + 1] - f.centre.y, z = base[i * 3 + 2] - f.centre.z;
  const len = Math.hypot(x, y, z) || 1;
  if (len > f.radius * 2.2) return [0, 0];
  const dx = x / len, dy = y / len, dz = z / len;
  let h = 0;
  if (hair !== 'none') {
    // hairline: high on the forehead (dy ~0.6 at the front), dropping over the temples and to
    // the nape at the back; the ears stay clear
    let line = dz > 0 ? 0.16 + 0.48 * dz - 0.1 * Math.abs(dx) * dz : 0.16 + 0.55 * dz;
    let ears = Math.abs(dx) > 0.78 && dy < 0.25 ? 0 : 1;
    if (hair === 'under') line += 0.06;
    if (hair === 'afro') { // (same shape as the afro's shell, tools/build_human.py make_afro: lower at the sides and back)
      line -= 0.3 * (1 - Math.max(0, dz));
      ears = 1 - smooth(0.66, 0.84, Math.abs(dx)) * smooth(0.05, -0.2, dy);
    }
    if (hair === 'taper') {
      // the line-up: dead straight across the forehead, squared at the temples (the corner drops into the
      // sideburn), a sharp arc round the ear, the nape squared off; every edge crisp (a barber's line)
      const ax = Math.abs(dx);
      line = dz > 0.3 ? (ax < 0.52 ? 0.5 : 0.5 - 0.65 * smooth(0.52, 0.6, ax)) : -0.05 - 0.25 * Math.max(0, dz) - 0.35 * Math.max(0, -dz); // (sides at the top of the ear, the back down to the nape)
      const ear = ax > 0.74 && dy < 0.32 && dy > -0.45 && dz > -0.45 && dz < 0.35 ? 0 : 1; // (the cut-out round the ear)
      const nape = dz < -0.5 && dy < -0.25 ? 0 : 1;
      h = smooth(line - 0.004, line + 0.012, dy) * ear * nape;
      // the taper: dense on top, thinning towards the ear and the nape (skin shows through at the bottom)
      h *= 0.35 + 0.65 * smooth(-0.15, 0.45, dy);
    } else h = smooth(line - 0.02, line + 0.07, dy) * ears;
    if (hair === 'fade') h *= smooth(-0.2, 0.45, dy); // skin shows through at the sides and back
  }
  let b = 0;
  if (beard !== 'none' && dz > -0.25) {
    // beard follows the face: from just under the nose at the front, rising along the jaw into
    // sideburns at the ears; down under the chin, fading on the neck
    const px = base[i * 3], py = base[i * 3 + 1];
    const lateral = Math.min(1, Math.abs(px - f.centre.x) / (f.halfWidth || 1));
    const top = f.noseTipY - 0.18 + Math.pow(lateral, 2.2) * 0.75;
    const below = smooth(top + 0.04, top - 0.08, py);
    const neck = smooth(f.chinY - 0.55, f.chinY - 0.1, py);
    const front = smooth(-0.25, 0.1, dz) * (1 - smooth(0.92, 1.0, lateral) * smooth(0.3, 0.0, dz));
    // keep the lips clear (a full beard still frames them)
    const mouthY = f.noseTipY - 0.42;
    const lips = Math.abs(py - mouthY) < 0.13 && lateral < 0.36 && dz > 0.6 ? (beard === 'full' ? 0.3 : 0.0) : 1;
    b = below * neck * front * lips;
    if (beard === 'stubble') b *= 0.55;
  }
  return [h, b];
}

const vert = /* glsl */ `
  attribute vec3 cover; attribute vec4 hdir; varying vec3 vCover; varying vec4 vDir; varying vec2 vUv;
  void main() { vCover = cover; vDir = hdir; vUv = uv; gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0); }`;
const frag = /* glsl */ `
  varying vec3 vCover; varying vec4 vDir; varying vec2 vUv; uniform vec2 uDensity; uniform float uTaper; uniform float uCorn;
  // cornrows: rows running front to back (planes through the head's front-back axis), each a plait: over-unders
  // along it, the scalp showing in the parting between rows
  float corn(vec3 d) {
    float a = atan(d.x, d.y + 0.25) * 5.5, f = fract(a);
    float row = smoothstep(0.1, 0.24, f) * smoothstep(0.9, 0.76, f);
    float t = d.z * 34.0 + (f > 0.5 ? 0.5 : 0.0);
    float plait = 0.72 + 0.28 * smoothstep(0.15, 0.5, abs(fract(t) - 0.5) + abs(f - 0.5) * 0.6);
    return row * plait;
  }
  // the taper's line-up, per pixel (from the direction on the head): a barber's edge whatever the mesh density
  float taper(vec3 d) {
    float ax = abs(d.x);
    // (the front sits under the curly top's edge; the temples square off beside the eyes and drop into the sideburn)
    float line = d.z > 0.3 ? 0.7 - 0.86 * smoothstep(0.78, 0.83, ax) * (1.0 - smoothstep(0.42, 0.55, d.z)) : -0.05 - 0.25 * max(0.0, d.z) - 0.35 * max(0.0, -d.z); // (nothing that faces forward: never by the eyes)
    float h = smoothstep(line - 0.004, line + 0.004, d.y);
    // the arc round the ear, and the nape squared off
    float e = pow((d.y + 0.06) / 0.38, 2.0) + pow((d.z + 0.05) / 0.4, 2.0);
    h *= 1.0 - (1.0 - smoothstep(0.96, 1.02, e)) * smoothstep(0.66, 0.7, ax);
    h *= 1.0 - smoothstep(-0.52, -0.48, -d.z) * smoothstep(-0.23, -0.27, d.y);
    // the fade: thick on top, thinning to skin by the ear and the nape
    return h * (0.3 + 0.7 * smoothstep(-0.2, 0.45, d.y));
  }
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  void main() {
    // stubble: dense random dots at the texture's scale, coverage from the vertex masks
    float n = hash(floor(vUv * 2048.0)) * 0.6 + hash(floor(vUv * 700.0)) * 0.4;
    float hc = uTaper > 0.5 && vDir.w > 0.5 ? taper(normalize(vDir.xyz)) : vCover.x;
    float hair = uCorn > 0.5 && vDir.w > 0.5 ? vCover.x * corn(normalize(vDir.xyz)) : hc * smoothstep(1.0 - uDensity.x, 1.0, n + 0.25);
    float beard = vCover.y * smoothstep(1.0 - uDensity.y, 1.0, n + 0.2);
    // fishnet: a diamond lattice of thin strands in UV space (B), only where the stocking covers
    vec2 g = vUv * 190.0;
    float da = abs(fract((g.x + g.y) * 0.5) - 0.5), db = abs(fract((g.x - g.y) * 0.5) - 0.5);
    float net = 1.0 - smoothstep(0.07, 0.16, min(da, db));
    gl_FragColor = vec4(hair, beard, vCover.z * net, 1.0);
  }`;

/** Render the hair/beard mask (R = hair, G = beard) into a texture in the body's UV space. */
export function renderPaintMask(renderer: THREE.WebGLRenderer, uv: Float32Array, index: THREE.BufferAttribute, cover: Float32Array,
  hair: PaintedHair, beard: Beard, target?: THREE.WebGLRenderTarget, dirs?: Float32Array): THREE.WebGLRenderTarget {
  const rt = target ?? new THREE.WebGLRenderTarget(1024, 1024, { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(uv.length / 2 * 3), 3));
  geo.setAttribute('cover', new THREE.BufferAttribute(cover, 3));
  geo.setAttribute('hdir', new THREE.BufferAttribute(dirs ?? new Float32Array(uv.length * 2), 4));
  geo.setIndex(index);
  const density = new THREE.Vector2(
    { none: 0, lowcut: 0.9, fade: 0.85, buzz: 0.97, afro: 0.97, taper: 0.97, cornrows: 1, under: 1 }[hair] ?? 0,
    { none: 0, stubble: 0.55, short: 0.9, full: 0.98 }[beard],
  );
  const mat = new THREE.ShaderMaterial({ vertexShader: vert, fragmentShader: frag, uniforms: { uDensity: { value: density }, uTaper: { value: hair === 'taper' ? 1 : 0 }, uCorn: { value: hair === 'cornrows' ? 1 : 0 } }, side: THREE.DoubleSide, depthTest: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(mesh);
  const prev = renderer.getRenderTarget();
  const clear = renderer.getClearColor(new THREE.Color()), alpha = renderer.getClearAlpha();
  renderer.setRenderTarget(rt);
  renderer.setClearColor(0x000000, 1);
  renderer.clear();
  renderer.render(scene, new THREE.Camera());
  renderer.setRenderTarget(prev);
  renderer.setClearColor(clear, alpha);
  geo.dispose();
  mat.dispose();
  return rt;
}

/** Makeup worn on the face: colours as css strings (missing: none) */
export interface Makeup { lips?: string; shadow?: string; liner?: boolean; blush?: string; gloss?: boolean }
/** the makeup's uniforms on a skin material: the mask (R lips, G eyeshadow, B blush, A liner), the colours, how strong each */
export interface MakeupUniforms { mask: { value: THREE.Texture | null }; lips: { value: THREE.Color }; shadow: { value: THREE.Color }; blush: { value: THREE.Color }; amt: { value: THREE.Vector4 }; gloss: { value: number } }
export const makeupUniforms = (): MakeupUniforms => ({ mask: { value: null }, lips: { value: new THREE.Color() }, shadow: { value: new THREE.Color() }, blush: { value: new THREE.Color() }, amt: { value: new THREE.Vector4() }, gloss: { value: 0 } });

const mkVert = /* glsl */ `
  attribute vec4 cover;
  varying vec4 vCover;
  void main() { vCover = cover; gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0); }`;
const mkFrag = /* glsl */ `
  varying vec4 vCover;
  void main() { gl_FragColor = vCover; }`;

/** Render the makeup mask (smooth: no stubble) into the body's UV space. */
export function renderMakeupMask(renderer: THREE.WebGLRenderer, uv: Float32Array, index: THREE.BufferAttribute, cover: Float32Array, target?: THREE.WebGLRenderTarget): THREE.WebGLRenderTarget {
  const rt = target ?? new THREE.WebGLRenderTarget(1024, 1024, { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(uv.length / 2 * 3), 3));
  geo.setAttribute('cover', new THREE.BufferAttribute(cover, 4));
  geo.setIndex(index);
  const mat = new THREE.ShaderMaterial({ vertexShader: mkVert, fragmentShader: mkFrag, side: THREE.DoubleSide, depthTest: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  const scene = new THREE.Scene();
  scene.add(mesh);
  const prev = renderer.getRenderTarget();
  const clear = renderer.getClearColor(new THREE.Color()), alpha = renderer.getClearAlpha();
  renderer.setRenderTarget(rt);
  renderer.setClearColor(0x000000, 0);
  renderer.clear();
  renderer.render(scene, new THREE.Camera());
  renderer.setRenderTarget(prev);
  renderer.setClearColor(clear, alpha);
  geo.dispose();
  mat.dispose();
  return rt;
}

/** Make a skin material mix the painted hair/beard colour from the mask, and the makeup over that. */
export function attachPaint(material: THREE.MeshStandardMaterial, mask: { value: THREE.Texture | null }, colour: { value: THREE.Color }, mk?: MakeupUniforms) {
  const m = mk ?? makeupUniforms();
  material.onBeforeCompile = (s) => {
    s.uniforms.uPaintMask = mask;
    s.uniforms.uPaintOn = { get value() { return !!mask.value; } };
    s.uniforms.uPaintColour = colour;
    s.uniforms.uMk = m.mask;
    s.uniforms.uMkOn = { get value() { return !!m.mask.value; } };
    s.uniforms.uMkLips = m.lips; s.uniforms.uMkShadow = m.shadow; s.uniforms.uMkBlush = m.blush; s.uniforms.uMkAmt = m.amt; s.uniforms.uMkGloss = m.gloss;
    s.fragmentShader = s.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform sampler2D uPaintMask; uniform vec3 uPaintColour; uniform bool uPaintOn;\nuniform sampler2D uMk; uniform bool uMkOn; uniform vec3 uMkLips; uniform vec3 uMkShadow; uniform vec3 uMkBlush; uniform vec4 uMkAmt; uniform float uMkGloss;')
      .replace('#include <map_fragment>', `#include <map_fragment>
        float lipAmt = 0.0, paintAmt = 0.0;
        #ifdef USE_MAP
          vec3 paint = uPaintOn ? texture2D(uPaintMask, vMapUv).rgb : vec3(0.0);
          paintAmt = clamp(max(paint.r, paint.g) * 0.92, 0.0, 1.0);
          diffuseColor.rgb = mix(diffuseColor.rgb, uPaintColour, paintAmt);
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.02, 0.018, 0.02), clamp(paint.b, 0.0, 1.0) * 0.95); // the net
          if (uMkOn) {
            vec4 mk = texture2D(uMk, vMapUv);
            // eyeshadow and blush tint the skin (a deep skin stays deep under them); lipstick covers; liner is near black
            float lum = dot(diffuseColor.rgb, vec3(0.3, 0.59, 0.11));
            diffuseColor.rgb = mix(diffuseColor.rgb, mix(uMkShadow * (0.45 + lum * 1.3), uMkShadow, 0.35), clamp(mk.g * uMkAmt.y, 0.0, 1.0));
            diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * uMkBlush * 1.6, clamp(mk.b * uMkAmt.z, 0.0, 1.0));
            lipAmt = clamp(mk.r * uMkAmt.x, 0.0, 1.0);
            diffuseColor.rgb = mix(diffuseColor.rgb, mix(uMkLips * (0.45 + lum), uMkLips, 0.4), lipAmt); // (a deep skin keeps its depth under the colour)
            diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.025, 0.02, 0.025), clamp(mk.a * uMkAmt.w, 0.0, 1.0));
          }
        #endif`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
        roughnessFactor = mix(roughnessFactor, 0.95, paintAmt); // hair is matte, skin has a sheen
        roughnessFactor = mix(roughnessFactor, mix(0.55, 0.18, uMkGloss), lipAmt); // (lipstick a satin, gloss shines)`);
  };
  material.customProgramCacheKey = () => 'skin-paint-mk';
}
